import { CUSTOMER_API_PREFIX } from '../contracts/customer-constants.js';
import type {
  CustomerPayResultDto,
  CustomerReceiptResultDto,
  CustomerRequestPaymentDto,
} from '../contracts/customer-auth.js';
import type { ResolvedCustomerClientConfig } from './config.js';
import { CustomerApiError } from './errors.js';
import { HttpTransport } from './http.js';

/**
 * The payment API, as the widget sees it.
 *
 * Payment is the one step of the customer journey that leaves the page, so this
 * class is deliberately thin: it asks Core to start (or resume) the payment for
 * one of the caller's own requests and returns the gateway URL Core chose. The
 * browser never builds a gateway URL, never sees an amount it could tamper with,
 * and never talks to the gateway directly — Core owns all three.
 *
 * While the gateway is under maintenance Core answers `mode: 'card_transfer'`
 * instead, with the destination card from its own configuration and no payment
 * attempt; the widget then asks for a receipt rather than redirecting.
 *
 * Retrying is safe by construction: the server reuses a live attempt and only
 * starts a new one after a terminal failure, and one request can never have two
 * live payments. A second tap is therefore a resume, not a second charge.
 *
 * Only the same-site flow (Tamoomeh) opens the gateway directly. A relying site
 * reaches the same endpoints through its own bridge, which is why `available`
 * is false until that bridge endpoint is configured.
 */
export class CustomerPayments {
  constructor(
    private readonly config: ResolvedCustomerClientConfig,
    private readonly transport: HttpTransport,
  ) {}

  /** True when this deployment can take the customer into the payment flow. */
  get available(): boolean {
    return this.config.authMode === 'same-site' || Boolean(this.config.bridge?.paymentUrl);
  }

  /**
   * True when this deployment can accept a manual payment receipt.
   *
   * The same-site flow (Tamoomeh) talks to Core directly. A relying site only
   * gets the fallback once its bridge exposes a `receiptUrl`, because the
   * browser cannot reach Core's customer API across registrable domains.
   */
  get receiptAvailable(): boolean {
    return this.config.authMode === 'same-site' || Boolean(this.config.bridge?.receiptUrl);
  }

  /**
   * Upload a manual payment receipt («رسید پرداخت») for one of the customer's
   * own requests, as the fallback for an unavailable gateway.
   *
   * Multipart, so the browser sets the boundary; the customer session cookie and
   * the CSRF header are added by the transport, exactly as for `pay()`.
   */
  async submitReceipt(
    requestReference: string,
    input: { file: Blob; description?: string },
  ): Promise<CustomerReceiptResultDto> {
    const reference = requestReference.trim();
    if (!reference) {
      throw new CustomerApiError('VALIDATION_ERROR', 'شناسه درخواست برای ثبت رسید مشخص نیست.');
    }

    const form = new FormData();
    const name =
      typeof File !== 'undefined' && input.file instanceof File && input.file.name
        ? input.file.name
        : 'receipt';
    form.append('receipt', input.file, name);
    if (input.description) form.append('description', input.description);

    let data: CustomerReceiptResultDto;
    try {
      ({ data } = await this.transport.send<CustomerReceiptResultDto>({
        path: this.receiptPath(reference),
        method: 'POST',
        target: this.target,
        form,
        // An upload is expensive and never idempotent on retry, so a transport
        // failure is surfaced rather than swallowed and repeated.
        retryable: false,
      }));
    } catch (error) {
      const normalized = error instanceof CustomerApiError ? error : CustomerApiError.unknown();
      this.config.onEvent?.({
        name: 'receipt.failed',
        detail: { requestPublicId: reference, code: normalized.code },
      });
      throw normalized;
    }

    this.config.onEvent?.({
      name: 'receipt.submitted',
      detail: {
        requestPublicId: reference,
        submissionPublicId: data.submission?.publicId,
      },
    });
    return data;
  }

  /** Start, or resume, payment for one of the customer's own requests. */
  async pay(requestReference: string): Promise<CustomerPayResultDto> {
    const reference = requestReference.trim();
    if (!reference) {
      throw new CustomerApiError('VALIDATION_ERROR', 'شناسه درخواست برای پرداخت مشخص نیست.');
    }

    let data: CustomerPayResultDto;
    try {
      ({ data } = await this.transport.send<CustomerPayResultDto>({
        path: this.paymentPath(reference, 'pay'),
        method: 'POST',
        target: this.target,
        // A create is never retried implicitly: a duplicate payment attempt is
        // avoided by the server's live-payment reuse, but a blind network retry
        // here would only add latency to a flow that redirects away anyway.
        retryable: false,
      }));
    } catch (error) {
      const normalized = error instanceof CustomerApiError ? error : CustomerApiError.unknown();
      this.config.onEvent?.({
        name: 'payment.failed',
        detail: { requestPublicId: reference, code: normalized.code },
      });
      throw normalized;
    }

    this.config.onEvent?.({
      name: 'payment.initiated',
      detail: {
        requestPublicId: data.request?.publicId,
        paymentPublicId: data.payment?.publicId,
        mode: data.mode ?? 'gateway',
      },
    });
    return data;
  }

  /** The authoritative payment state for one of the customer's own requests. */
  async status(requestReference: string): Promise<CustomerRequestPaymentDto> {
    const reference = requestReference.trim();
    if (!reference) {
      throw new CustomerApiError('VALIDATION_ERROR', 'شناسه درخواست مشخص نیست.');
    }
    const { data } = await this.transport.send<CustomerRequestPaymentDto>({
      path: this.paymentPath(reference, 'payment'),
      method: 'GET',
      target: this.target,
    });
    return data;
  }

  private get target(): 'authority' | 'bridge' {
    return this.config.authMode === 'sso' ? 'bridge' : 'authority';
  }

  /**
   * The endpoint for one operation.
   *
   * On the same-site path both endpoints are Core's; on the relying-site path
   * they are the site's own bridge, which holds the Core session server-side.
   * `paymentUrl` is the bridge's base for both, matching how `requestUrl` and
   * `listUrl` are configured for requests.
   */
  private paymentPath(reference: string, operation: 'pay' | 'payment'): string {
    const encoded = encodeURIComponent(reference);
    if (this.config.authMode === 'sso') {
      const bridge = this.config.bridge;
      if (!bridge) {
        throw new CustomerApiError(
          'BRIDGE_UNAVAILABLE',
          'پرداخت از این سایت پیکربندی نشده است.',
        );
      }
      if (!bridge.paymentUrl) {
        throw new CustomerApiError(
          'BRIDGE_UNAVAILABLE',
          'پرداخت از این سایت پیکربندی نشده است.',
        );
      }
      const separator = bridge.paymentUrl.includes('?') ? '&' : '?';
      const base = `${bridge.paymentUrl}${separator}request=${encoded}`;
      return operation === 'pay' ? `${base}&action=pay` : base;
    }
    return operation === 'pay'
      ? `${CUSTOMER_API_PREFIX}/requests/${encoded}/pay`
      : `${CUSTOMER_API_PREFIX}/requests/${encoded}/payment`;
  }

  /**
   * The receipt-upload endpoint for one request.
   *
   * Same-site posts straight to Core. A relying site must configure a bridge
   * `receiptUrl`; without one `receiptAvailable` is false and the widget never
   * calls this, so the throw is a defensive backstop rather than a user path.
   */
  private receiptPath(reference: string): string {
    const encoded = encodeURIComponent(reference);
    if (this.config.authMode === 'sso') {
      const bridge = this.config.bridge;
      if (!bridge?.receiptUrl) {
        throw new CustomerApiError(
          'BRIDGE_UNAVAILABLE',
          'ارسال رسید پرداخت از این سایت پیکربندی نشده است.',
        );
      }
      const separator = bridge.receiptUrl.includes('?') ? '&' : '?';
      return `${bridge.receiptUrl}${separator}request=${encoded}`;
    }
    return `${CUSTOMER_API_PREFIX}/requests/${encoded}/receipt`;
  }
}
